import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import Breadcrumbs from '../components/common/Breadcrumbs';
import { useCookieConsent } from '../hooks/useCookieConsent';

const CookiePolicy = () => {
  const { openSettingsModal } = useCookieConsent();
  const pStyle = "mb-4 leading-relaxed text-sm sm:text-base text-gray-700";
  const h2Style = "text-lg sm:text-xl font-bold text-gray-900 border-b-2 border-blue-200 pb-2 mb-4 mt-8";
  const ulStyle = "list-disc list-inside space-y-2 pl-4 mb-4 text-sm sm:text-base text-gray-700";

  return (
    <>
      <SEO
        title="Cookieポリシー | Billwave"
        description="株式会社シグナルが運営する「Billwave」のCookieポリシーです。Cookieの利用目的、Cookieの種類、保存期間、設定の変更方法に関する情報を明記しています。"
        path="/cookie-policy"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumbs items={[{ label: 'Cookieポリシー' }]} />

        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 text-center mb-8">
            Cookieポリシー
          </h1>

          <p className={pStyle}>
            株式会社シグナル（以下「当社」といいます）は、当社が提供する「Billwave」（以下「本サービス」といいます）の利便性向上、セッションの維持、利用状況の分析のためにCookieおよび類似の技術を使用しています。
          </p>

          <h2 className={h2Style}>１．Cookie（クッキー）とは</h2>
          <p className={pStyle}>
            Cookieとは、ウェブサイトを訪問した際にお客様の端末（パソコン、スマートフォン、タブレット等）に保存される小さなテキストファイルです。Cookieを使用することで、ウェブサイトはお客様の端末を識別し、次回アクセス時の入力の手間を省いたり、利用状況に応じた最適なサービスを提供したりすることが可能になります。
          </p>

          <h2 className={h2Style}>２．利用目的</h2>
          <p className={pStyle}>
            当社は、以下の目的でCookieを利用します。
          </p>
          <ul className={ulStyle}>
            <li>本サービスにおけるログイン状態の維持およびセッション管理</li>
            <li>不正アクセスの防止およびセキュリティ（CSRF対策等）の確保</li>
            <li>アクセス状況の計測およびサービスの品質向上・改善</li>
          </ul>

          <h2 className={h2Style}>３．利用するCookieの種類</h2>
          <p className={pStyle}>
            本サービスでは、以下のカテゴリのCookieを使用しています。
          </p>

          <div className="space-y-4 my-6">
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="font-bold text-gray-900 mb-1">① 必須Cookie（Necessary Cookies）</div>
              <p className="text-sm text-gray-600 leading-relaxed">
                本サービスが正常に動作するために不可欠なCookieです。ユーザー認証、セッション維持、セキュリティ管理に使用されます。これらを無効にすると本サービスを正しく利用できなくなるため、常に有効となります。
              </p>
            </div>

            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="font-bold text-gray-900 mb-1">② 解析Cookie（Analytics Cookies）</div>
              <p className="text-sm text-gray-600 leading-relaxed">
                訪問者数やトラフィックソースなどを計測し、本サービスのパフォーマンスや機能改善のために使用するCookieです。お客様の同意を得た場合のみ有効化されます。
              </p>
            </div>
          </div>

          <h2 className={h2Style}>４．保存期間</h2>
          <p className={pStyle}>
            Cookieには、ブラウザを閉じるまで保持される「セッションCookie」と、指定された期間（最大2年間など）または手動で削除されるまで端末に保存される「永続Cookie」があります。
          </p>

          <h2 className={h2Style}>５．Cookie設定の変更方法</h2>
          <p className={pStyle}>
            お客様は、いつでも当サイト上の設定バナーまたはフッターにある「Cookie設定」リンクから、解析Cookieの有効/無効を変更することができます。
          </p>
          <div className="my-4">
            <button
              onClick={openSettingsModal}
              className="px-5 py-2.5 bg-blue-600 text-white font-semibold text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              Cookie設定を変更する
            </button>
          </div>
          <p className={pStyle}>
            また、お使いのブラウザの設定を変更することで、すべてのCookieを拒否または個別で削除することも可能です。
          </p>

          <h2 className={h2Style}>６．お問い合わせ先</h2>
          <p className={pStyle}>
            Cookieポリシーに関するご質問やご相談につきましては、お問い合わせフォームよりご連絡ください。
          </p>

          <h2 className={h2Style}>７．プライバシーポリシー</h2>
          <p className={pStyle}>
            当社の個人情報の取り扱い全体については、{' '}
            <Link to="/privacy-policy" className="text-blue-600 underline font-semibold hover:text-blue-800">
              個人情報保護方針（プライバシーポリシー）
            </Link>{' '}
            をご参照ください。
          </p>

          <div className="text-right mt-10 pt-6 border-t border-gray-200 text-sm text-gray-600">
            <p>2026年8月6日 改定</p>
            <p className="font-bold mt-1 text-gray-800">株式会社シグナル</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePolicy;
